import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import { flow, forEach, last, sumBy } from 'lodash';
import { FormattedNumber } from 'react-intl';
import FaSpinner from 'react-icons/fa/spinner';
import { IStore } from '../../../../redux/IStore';
import { Row, Col, Button } from 'react-bootstrap';
import { IAuthState } from '../../../../services/auth/models';
import { reduxForm, formValueSelector, destroy } from 'redux-form';
import { IBrandingActions, IBrandingState } from '../../../../services/branding/models';
import {IPolicy, IPolicyApplication, ITransaction, QuoteStatus} from '../../../../data/api/models';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { actions as brandingActions } from '../../../../services/branding/reducer';
import { actions as authActions, IAuthActions } from '../../../../services/auth/reducer';
import {dateFromApi, translateStatus} from '../../../../util/helpers';
import steps from '../../../Quote/steps';
import * as moment from 'moment';

interface IPolicyDetailProps {
  location: any;
  auth: IAuthState;
  quote: IQuoteState;
  branding: IBrandingState;
  actions: {
    calculate: any;
    auth: IAuthActions;
    quote: IQuoteActions;
    destroy(formName: string);
    branding: IBrandingActions,
    push: (route: string) => void;
  };
}

interface IPolicyDetailStates {
  isRetrievingQuote: boolean;
}

class PolicyDetail extends React.Component<IPolicyDetailProps, IPolicyDetailStates> {
  public state = {
    isRetrievingQuote: false,
  };

  private handleGetNewQuote = () => {
    const { actions } = this.props;

    actions.destroy('quote');
    actions.quote.reset();
    actions.push('/');
  }

  private handleEditQuote = () => {
    const { actions, auth, quote, branding } = this.props;

    this.setState({
      isRetrievingQuote: true,
    });

    let selectedQuote: IPolicyApplication = null;

    forEach(auth.customer.policyApplications, (quoteSelected, level) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
      }
    });
    forEach(auth.customer.renewals, (quoteSelected, level) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
      }
    });

    // Re-init form
    actions.destroy('quote');
    actions.quote.reset();
    const product = actions.branding.getProduct();
    const lastStep = last(selectedQuote.steps);
    const quoteSteps = steps(branding.channel);
    let route = null;

    quoteSteps.forEach((step) => {
      if (step.key !== lastStep) {
        return;
      }

      route = step.route;
    });

    actions.quote.initFromProduct(product, quote, branding);

    // Set some data
    actions.quote.retrieveQuote(selectedQuote);
    actions.quote.startQuoteProcess();

    // Recalculate so that we can get fill up the quotes available for the policy
    actions.quote.calculate(selectedQuote, false).then(() => {
      // define an initial step if user is retrieving quote from b2b
      if (!selectedQuote.steps) {
        actions.quote.setSteps([quoteSteps[0].key]);
      }

      actions.push(route ? route : '/');
    });
  }

  private renderCoverDates = (isDisplayingPolicy, policy, selectedQuote, isDisplayingRenewal) => {
    const policyDates = (policy) => {
      return <Col sm={8} xs={6} className="info">{policy.commencesAt} - {policy.expiresAt}</Col>;
    };

    const quoteDates = (selectedQuote) => {
      return <Col sm={8} xs={6} className="info">{selectedQuote.startDate} - {dateFromApi(selectedQuote.endDate)}</Col>;
    };

    const cancellationDate = (policy) => {
      return <Col sm={8} xs={6} className="info">{policy.cancelledAt}</Col>;
    };

    return (
      <div>
        <Col sm={12} className="summary-info">
          <Row>
            <Col sm={4} xs={6}>
              Cover Dates:
            </Col>
            {isDisplayingPolicy ? policyDates(policy) : quoteDates(selectedQuote)}
          </Row>
        </Col>
        { policy && policy.cancelledAt && (
          <Col sm={12} className="summary-info">
            <Row>
              <Col sm={4} xs={6}>
                Cancellation Date:
              </Col>
              {cancellationDate(policy)}
            </Row>
          </Col>
        )}
      </div>
    );
  }

  private renderInsuredDetails = (isDisplayingPolicy, policy, selectedQuote) => {
    const policyOptions = (policy) => {
      return (
        <Col sm={8} xs={6} className="info">
          {policy.insured}
        </Col>
      );
    };

    const quoteOptions = (selectedQuote) => {
      const personalName = selectedQuote.insured.firstName === 'Test' || selectedQuote.insured.lastName === 'Test' ? '-'
        : (selectedQuote.insured.firstName + ' ' + selectedQuote.insured.lastName);

      return (
        <Col sm={8} xs={6} className="info">
          {selectedQuote.insured.companyName ? (
            <span>{selectedQuote.insured.companyName}</span>
          ) : (
            <span>
              {personalName}
            </span>
          )}
        </Col>
      );
    };

    return (
      <Col sm={12} className="summary-info">
        <Row>
          <Col sm={4} xs={6}>
            Insured(s):
          </Col>
          {isDisplayingPolicy ? policyOptions(policy) : quoteOptions(selectedQuote)}
        </Row>
      </Col>
    );
  }

  private renderStatus = (isDisplayingPolicy, policy, selectedQuote) => {
    return (
      <Col sm={12} className="summary-info">
        <Row>
          <Col sm={4} xs={6}>
            Status:
          </Col>
          <Col sm={8} xs={6} className="info">
            {isDisplayingPolicy ? policy.status : selectedQuote.status}
          </Col>
        </Row>
      </Col>
    );
  }

  private renderPremium = (isDisplayingPolicy, policy, selectedQuote) => {
    let show = true;
    if (!isDisplayingPolicy && (!selectedQuote.steps || !selectedQuote.steps.includes('quote'))) {
      show = false;
    }

    const premium = (amount, show) => {
      return amount && show ? (
        <Row>
          <Col sm={4} xs={6}>
            Premium:
          </Col>
          <Col sm={8} xs={6} className="info">
            <FormattedNumber
              value={amount}
              style="currency"
              currency="GBP"
            />
          </Col>
        </Row>
      ) : (<div />);
    };

    const returnPremium = (amount) => {
      return amount ? (
        <Col sm={12} className="summary-info">
          <Row>
            <Col sm={4} xs={6}>
              Return premium:
            </Col>
            <Col sm={8} xs={6} className="info">
              <FormattedNumber
                value={amount}
                style="currency"
                currency="GBP"
              />
            </Col>
          </Row>
        </Col>
      ) : (<div />);
    };

    let fullAmount = 0;
    if (isDisplayingPolicy) {
      fullAmount = policy.totalPremiumValue;
    } else if (selectedQuote.premiums.length) {
      fullAmount = selectedQuote.premiums[0].gross;
    }
    let returnAmount = 0;
    if (isDisplayingPolicy) {
      const returnTransactions = policy.transactions.filter((item) => {
        return item.amount < 0;
      });
      returnAmount = returnTransactions.length ? sumBy(returnTransactions, (item) => item.amount) : 0;
    }

    return (
      <div>
        <Col sm={12} className="summary-info">
          {premium(fullAmount, show)}
        </Col>
        {returnPremium(returnAmount)}
      </div>
    );
  }

  private renderDocuments = (isDisplayingPolicy, policy, selectedQuote, isDisplayingRenewal) => {
    const documents = (documents) => {
      if (!documents.length) {
        return null;
      }

      let fullAmount = 0;
      if (isDisplayingPolicy) {
        fullAmount = policy.totalPremiumValue;
      } else if (selectedQuote.premiums.length) {
        fullAmount = selectedQuote.premiums[0].gross;
      }
      if (!fullAmount || (!isDisplayingPolicy && !isDisplayingRenewal &&
        (!selectedQuote.steps || !selectedQuote.steps.includes('quote')))) {
        return null;
      }

      const documentsSorted = [];
      const names = isDisplayingPolicy ? [
        'Agricultural Vehicle Schedule',
        'Agricultural Vehicle New Business Schedule',
        'Agricultural Vehicle Certificate',
        'Agricultural Vehicle New Business Certificate',
        'Agricultural Vehicle Statement of Fact',
        'Policy Wording: Agricultural Vehicle',
        'IPID: Agricultural Vehicle',
        'Terms of Business: Agricultural Vehicle BJP',
      ] : [
        'Quotation BJP Agricultural Vehicle',
        'Agricultural Vehicle Statement of Fact',
        'Policy Wording: Agricultural Vehicle',
        'IPID: Agricultural Vehicle',
        'Terms of Business: Agricultural Vehicle BJP',
      ];
      names.map((name) => {
        const doc = documents.find((document) => {
          return document.displayName === name;
        });
        if (doc) {
          switch (doc.name) {
            case 'Agricultural Vehicle Schedule':
            case 'Agricultural Vehicle New Business Schedule':
              doc.displayName = 'Schedule';
              break;
            case 'Quotation BJP Agricultural Vehicle':
              doc.displayName = 'Quotation';
              break;
            case 'Agricultural Vehicle Certificate':
            case 'Agricultural Vehicle New Business Certificate':
              doc.displayName = 'Certificate';
              break;
            case 'Agricultural Vehicle Statement of Fact':
              doc.displayName = 'Statement of Fact';
              break;
            case 'Policy Wording: Agricultural Vehicle':
              doc.displayName = 'Policy Wording';
              break;
            case 'IPID: Agricultural Vehicle':
              doc.displayName = 'Insurance Product Information Document';
              break;
            case 'Terms of Business: Agricultural Vehicle BJP':
              doc.displayName = 'Terms of Business';
              break;
          }

          documentsSorted.push(doc);
        }
      });

      return (
        <Row className={'documents'}>
          <Col sm={12}>
          {documentsSorted.map((document, index) => {
            return (
              <a
                key={index}
                className="btn-secondary btn"
                href={document.filePath}
                target="_blank"
              >
                {document.displayName}
              </a>
            );
          })}
          </Col>
        </Row>
      );
    };

    return (documents(isDisplayingPolicy ? policy.documents : selectedQuote.documents));
  }

  public render() {
    const { auth, location } = this.props;
    const { isRetrievingQuote } = this.state;

    let policy: IPolicy = null;
    let selectedQuote: IPolicyApplication = null;
    let isDisplayingPolicy = false;
    let isDisplayingRenewal = false;
    let policyNumber = null;

    forEach(auth.customer.policies, (policySelected, level) => {
      if (location.pathname.match(policySelected.id)) {
        policy = policySelected;
        isDisplayingPolicy = true;
        policyNumber = policy.policyNumber;
      }
    });

    forEach(auth.customer.policyApplications, (quoteSelected, level) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
        isDisplayingPolicy = false;
      }
    });

    forEach(auth.customer.renewals, (quoteSelected, level) => {
      if (location.pathname.match(quoteSelected.id)) {
        selectedQuote = quoteSelected;
        isDisplayingPolicy = false;
        isDisplayingRenewal = true;
        policyNumber = selectedQuote.renewingPolicyNumber;
      }
    });

    const showDocuments = (!isDisplayingPolicy || policy.status !== QuoteStatus.CANCELLED);

    return (
      <div className="page-container animated fadeIn">
        <div className="detail-section">
          <div className="customer-section-title">
            <h1>{isDisplayingPolicy || isDisplayingRenewal ?
              `${policyNumber}` :
              `${selectedQuote.quoteReference}`}</h1>
          </div>
          <Row>
            <Col sm={12}>
              <Row>
                {this.renderCoverDates(isDisplayingPolicy, policy, selectedQuote, isDisplayingRenewal)}
                {this.renderInsuredDetails(isDisplayingPolicy, policy, selectedQuote)}
                {this.renderStatus(isDisplayingPolicy, policy, selectedQuote)}
                {!isDisplayingRenewal && (
                  this.renderPremium(isDisplayingPolicy, policy, selectedQuote)
                )}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              { showDocuments && (
                <div>
                  <hr/>
                  {this.renderDocuments(isDisplayingPolicy, policy, selectedQuote, isDisplayingRenewal)}
                </div>
              )}
            </Col>
          </Row>
        </div>

        <div className="btn-bar section policy-detail">
          <Link to="/account/summary" className="btn btn-secondary customer-btn">
            Back
          </Link>
        </div>
      </div>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const endDate = selector(state, 'endDate');
      const startDate = selector(state, 'startDate');

      return {
        endDate,
        startDate,
        auth: state.auth,
        quote: state.quote,
        branding: state.branding,
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        destroy: bindActionCreators(destroy, dispatch),
        auth: bindActionCreators({...authActions}, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
      },
    }),
  ),
])(PolicyDetail);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/CustomerArea/scenes/PolicyDetail/index.tsx